import { StrictMode, lazy, Suspense, useMemo, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { App } from './App';
import { ThemeProvider } from './contexts/ThemeContext';
import { PodcastKeepaliveProvider, usePodcastKeepalive } from './contexts/PodcastKeepaliveContext';
import { MiniPlayerBar } from './components/MiniPlayerBar';
import { createQueryPersister, persistOptions } from './lib/query-persistence';
import { auth } from './services/auth';
import { useAuth } from './hooks/useAuth';
import { registerServiceWorker } from './lib/register-sw';
import './styles/globals.css';

function AccountQueries({
  userId,
  children,
}: {
  userId: string | null;
  children: React.ReactNode;
}) {
  const client = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 1000 * 60 * 5, retry: 2, refetchOnWindowFocus: false },
          mutations: { retry: 1 },
        },
      }),
    [],
  );
  const persister = useMemo(() => (userId ? createQueryPersister(userId) : null), [userId]);
  if (!persister) return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
  return (
    <PersistQueryClientProvider client={client} persistOptions={{ persister, ...persistOptions }}>
      {children}
    </PersistQueryClientProvider>
  );
}

function AccountBoundary({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [identity, setIdentity] = useState(() => auth.getUserId());
  useEffect(() => {
    const check = () => setIdentity(auth.getUserId());
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    check();
    return () => {
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [isAuthenticated]);
  const userId = isAuthenticated ? identity : null;
  return (
    <AccountQueries key={userId ?? 'anonymous'} userId={userId}>
      {children}
    </AccountQueries>
  );
}

registerServiceWorker();

// Lazy-load PodcastStudy — only fetched when user first opens a podcast
const PodcastStudy = lazy(() =>
  import('./pages/PodcastStudy').then((m) => ({ default: m.PodcastStudy })),
);

/**
 * Keeps PodcastStudy mounted across route changes so the YouTube iframe
 * (and its audio) never gets destroyed.
 *
 * When the user is on /podcasts/:id  → fixed overlay is VISIBLE (z-50)
 * When the user is elsewhere         → fixed overlay is HIDDEN via
 *   `visibility: hidden` (does NOT pause iframes, unlike display:none)
 *   and z-index: -1 (behind everything, no pointer capture).
 */
function PodcastKeepaliveLayer() {
  const { persistedPodcastId } = usePodcastKeepalive();
  const location = useLocation();

  const isPodcastDetailPage =
    location.pathname.startsWith('/podcasts/') && location.pathname !== '/podcasts';

  if (!persistedPodcastId) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        visibility: isPodcastDetailPage ? 'visible' : 'hidden',
        zIndex: isPodcastDetailPage ? 50 : -1,
        pointerEvents: isPodcastDetailPage ? 'auto' : 'none',
      }}
    >
      <Suspense fallback={null}>
        <PodcastStudy podcastIdProp={persistedPodcastId} />
      </Suspense>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <AccountBoundary>
        <BrowserRouter>
          <PodcastKeepaliveProvider>
            <App />
            <PodcastKeepaliveLayer />
            <MiniPlayerBar />
          </PodcastKeepaliveProvider>
        </BrowserRouter>
      </AccountBoundary>
    </ThemeProvider>
  </StrictMode>,
);
